import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import Image from "@Components/Image";
import VideoPlayer from "@Pages/PropertyPage/components/GalleryModal/components/VideoPlayer/VideoPlayer";
import {
  getVideoSrc,
  getVideoThumb,
} from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import getImagePath from "@Utils/getImagePath";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import PlayIcon from "@Icons/PlayIcon";
import VideoChevronLeftIcon from "@Icons/VideoChevronLeftIcon";
import ShortlistButton from "@Pages/Search/components/SearchListItem/components/ShortListButton";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import CountryFlag from "@Components/CountryFlag";
import { viewRoomsClickHandler } from "@Pages/PropertyPage/data/helper";
import ReviewCard from "../ReviewCard";
import { ReviewData } from "../../types";
import { getInitials, getNationalityCountryCode } from "../../utils";
import styles from "./reviewMediaGallery.module.scss";

/** Compact review card for the mobile gallery reviews tab */
const MobileGalleryReviewCard: React.FC<{
  review: ReviewData;
  isActive?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ review, isActive, expanded = false, onToggleExpand }) => {
  const { t } = useTranslationHook();
  const name = review.reviewer?.name || "";
  const text = review.body?.trim() || "";
  const { rating } = review;
  const avatar = review.reviewer?.avatar || "";
  const nationality = review.reviewer?.nationality || "";
  const countryCode = getNationalityCountryCode(nationality);

  const charLimit = 200;
  const isLong = text.length > charLimit;
  const truncatedContent = isLong ? `${text.slice(0, charLimit)}..` : text;
  const displayedContent = expanded ? text : truncatedContent;

  return (
    <div
      className={formatClassNames(
        styles.mobileReviewCard,
        isActive && styles.mobileReviewCardActive,
      )}
    >
      <div className={styles.mobileReviewCardTop}>
        <div className={styles.mobileReviewCardInfo}>
          <div className={styles.mobileReviewCardAvatar}>
            {avatar ? (
              <Image
                src={avatar}
                alt={name}
                width={32}
                height={32}
                className={styles.mobileReviewCardAvatarImg}
              />
            ) : (
              <span className={styles.mobileReviewCardInitials}>{getInitials(name || "A")}</span>
            )}
            {countryCode && (
              <div className={styles.mobileReviewCardFlag}>
                <CountryFlag countryCode={countryCode} width={16} height={16} />
              </div>
            )}
          </div>
          <div className={styles.mobileReviewCardUserDetails}>
            <span className={styles.mobileReviewCardName}>{name}</span>
            {review.bookingDate && (
              <span className={styles.mobileReviewCardDate}>{review.bookingDate}</span>
            )}
          </div>
        </div>
        {rating > 0 && (
          <div className={styles.mobileReviewCardRating}>
            {rating.toFixed(1)}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M6 1L7.545 4.13L11 4.635L8.5 7.07L9.09 10.51L6 8.885L2.91 10.51L3.5 7.07L1 4.635L4.455 4.13L6 1Z"
                fill="#ffffff"
              />
            </svg>
          </div>
        )}
      </div>
      {text && (
        <div className={styles.mobileReviewCardText}>
          {displayedContent}
          {isLong && onToggleExpand && (
            <span
              role="button"
              tabIndex={0}
              className={styles.mobileReviewCardReadMore}
              onClick={onToggleExpand}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onToggleExpand();
              }}
            >
              {" "}
              {expanded
                ? t("propertypage.reviewssection.readless")
                : t("propertypage.reviewssection.readmore")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

interface ReviewMediaItem {
  category?: string;
  path?: string;
  base_path?: string;
  thumbnail_url?: string;
  platform?: string;
  rating?: number;
  userName?: string;
  roomTypeName?: string;
}

interface ReviewMediaGalleryProps {
  media: ReviewMediaItem[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  isDesktop?: boolean;
  reviews?: ReviewData[];
  mediaIndexMap?: Map<string, number>;
  propertyName?: string;
  propertyId?: string | number;
}

const getThumbSrc = (item: ReviewMediaItem) =>
  item?.thumbnail_url || item?.base_path || item?.path || "";

const Media = ({
  item,
  isDesktop,
  eager,
}: {
  item: ReviewMediaItem;
  isDesktop: boolean;
  eager: boolean;
}) => {
  if (item?.category === "videos") {
    return (
      <VideoPlayer
        src={getVideoSrc(item)}
        thumbnailUrl={getVideoThumb(item)}
        isYoutube={item?.path?.includes("youtube")}
        autoPlay={false}
        fromGalleryCarousel
      />
    );
  }

  const imgModifier = isDesktop ? "gallery-full-screen-desktop" : "gallery-full-screen-mobile";
  return (
    <Image
      width="100%"
      height={isDesktop ? "100%" : "90%"}
      loading={eager ? "eager" : "lazy"}
      alt={item?.userName ? `Review media by ${item.userName}` : "Review media"}
      src={getImagePath(item?.base_path || item?.path || "", imgModifier, {}, true)}
      className={styles.media}
    />
  );
};

const NavArrow = ({ direction, onClick }: { direction: "prev" | "next"; onClick: () => void }) => (
  <button
    type="button"
    className={formatClassNames(
      styles.navButton,
      direction === "prev" ? styles.prevButton : styles.nextButton,
    )}
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous media" : "Next media"}
  >
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d={direction === "prev" ? "M10 12L6 8L10 4" : "M6 4L10 8L6 12"}
        stroke="#111928"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const ReviewMediaGallery: React.FC<ReviewMediaGalleryProps> = ({
  media,
  initialIndex = 0,
  isOpen,
  onClose,
  isDesktop = false,
  reviews = [],
  mediaIndexMap,
  propertyName = "",
  propertyId,
}) => {
  const total = media?.length || 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRailRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const safeIndex = total ? Math.min(Math.max(currentIndex, 0), total - 1) : 0;
  const currentItem = media?.[safeIndex];

  // Build reverse lookup: for a given media index → owning review id
  const activeReviewId = useMemo(() => {
    if (!mediaIndexMap || !reviews?.length) return null;
    const entries = Array.from(mediaIndexMap.entries()).sort((a, b) => b[1] - a[1]);
    for (const [reviewId, startIdx] of entries) {
      if (safeIndex >= startIdx) return reviewId;
    }
    return null;
  }, [mediaIndexMap, reviews, safeIndex]);

  const { inventoryId, inventory } = useSelector(
    ({ propertyPageData }: AppState) => ({
      inventoryId: propertyPageData?.data?.id,
      inventory: propertyPageData?.data,
    }),
    shallowEqual,
  );

  const { t } = useTranslationHook();

  // Lock background scroll while open
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    lockBody();
    return () => {
      unlockBody();
    };
  }, []);

  // Close on Escape + Arrow key navigation
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCurrentIndex((i) => Math.max(0, i - 1));
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setCurrentIndex((i) => Math.min(total - 1, i + 1));
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, total]);

  // Pause any playing video when the active item changes
  useEffect(() => {
    const videos = containerRef.current?.querySelectorAll("video");
    videos?.forEach((video) => {
      if (!video.paused) video.pause();
    });
    setReviewExpanded(false);
  }, [safeIndex]);

  // Auto-scroll thumbnail strip to keep the active thumbnail visible
  useEffect(() => {
    const rail = thumbRailRef.current;
    if (!rail) return;
    const activeThumb = rail.querySelector(`[data-thumb-index="${safeIndex}"]`) as HTMLElement;
    if (!activeThumb) return;
    activeThumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [safeIndex]);

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(total - 1, i + 1));

  if (!isOpen || total === 0) return null;

  const renderThumbnails = (variant: "desktop" | "mobile") =>
    media.map((item, index) => {
      const isThumbActive = index === safeIndex;
      return (
        <button
          key={`thumb-${item?.path || item?.base_path || index}`}
          type="button"
          data-thumb-index={index}
          className={
            variant === "desktop"
              ? formatClassNames(styles.deskThumb, isThumbActive && styles.deskThumbActive)
              : formatClassNames(styles.thumb, isThumbActive && styles.thumbActive)
          }
          onClick={() => setCurrentIndex(index)}
          aria-label={`View media ${index + 1}`}
        >
          <Image
            width={variant === "desktop" ? 200 : 56}
            height={variant === "desktop" ? 112 : 56}
            loading="lazy"
            alt=""
            src={getThumbSrc(item)}
            className={variant === "desktop" ? styles.deskThumbImg : styles.thumbImg}
          />
          {item?.category === "videos" && (
            <span className={variant === "desktop" ? styles.deskThumbPlay : styles.thumbPlay}>
              <span className={variant === "desktop" ? styles.deskThumbPlayIcon : undefined}>
                <PlayIcon width="12" height="12" fill={variant === "desktop" ? "#000" : "white"} />
              </span>
            </span>
          )}
        </button>
      );
    });

  // ===== Desktop layout (VideoPage-style two-column view) =====
  if (isDesktop) {
    return (
      <div
        className={styles.deskOverlayWrapper}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div
          ref={containerRef}
          className={styles.deskOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Review media gallery"
        >
          <header className={styles.deskHeader}>
            <div className={styles.deskHeaderLeft}>
              <button
                type="button"
                className={styles.backButton}
                onClick={onClose}
                aria-label="Close gallery"
              >
                <VideoChevronLeftIcon />
              </button>
              <h2 className={styles.deskTitle}>{propertyName}</h2>
            </div>
            <div className={styles.deskHeaderRight}>
              {(() => {
                const activeReview = reviews.find((r) => r.id === activeReviewId);
                const roomTypeName = activeReview?.roomType || currentItem?.roomTypeName;
                if (!roomTypeName) return null;
                return (
                  <button
                    type="button"
                    className={styles.deskViewRoomsButton}
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent("close-reviews-panel"));
                      setTimeout(() => {
                        viewRoomsClickHandler("review_gallery_desktop");
                      }, 300);
                    }}
                  >
                    {t("propertypage.components.gallerycarousel.viewrooms")}
                  </button>
                );
              })()}
              {inventoryId && (
                <ShortlistButton
                  inventoryId={inventoryId}
                  inventory={inventory}
                  noAbosolute
                  screenName={APP_PAGE.PROPERTY}
                  className={styles.iconButton}
                />
              )}
            </div>
          </header>

          <div className={styles.deskContent}>
            <div className={styles.deskLeft}>
              <div className={styles.deskPlayerWrap}>
                <div className={styles.deskPlayer}>
                  <Media item={currentItem} isDesktop eager />
                  {total > 1 && safeIndex > 0 && <NavArrow direction="prev" onClick={goPrev} />}
                  {total > 1 && safeIndex < total - 1 && (
                    <NavArrow direction="next" onClick={goNext} />
                  )}
                </div>
              </div>

              {total > 1 && (
                <div className={styles.deskRail} ref={thumbRailRef}>
                  {renderThumbnails("desktop")}
                </div>
              )}
            </div>

            <div className={styles.deskRight}>
              {reviews
                .filter((review) => review.id === activeReviewId)
                .map((review) => (
                  <div key={review.id} data-review-id={review.id}>
                    <ReviewCard
                      review={review}
                      fullWidth
                      showFullBody
                      onMediaClick={(index: number) => setCurrentIndex(index)}
                      mediaStartIndex={mediaIndexMap?.get(review.id) || 0}
                      propertyId={propertyId}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Mobile layout (VideoPage-style) =====
  const hasReviews = reviews.length > 0;

  // Swipe gesture handling for media navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    if (Math.abs(diff) < threshold) return;

    if (diff > 0 && safeIndex < total - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (diff < 0 && safeIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  return (
    <div className={styles.overlayWrapper} onWheel={(e) => e.stopPropagation()}>
      <div
        ref={containerRef}
        className={styles.overlay}
        role="dialog"
        aria-modal="true"
        aria-label="Review media gallery"
      >
        {/* Navbar */}
        <div className={styles.mobileNavbar}>
          <button
            type="button"
            className={styles.mobileBackButton}
            onClick={onClose}
            aria-label="Close gallery"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="#FFFFFF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className={styles.mobileCounter}>
            {safeIndex + 1}/{total}
          </span>
          <div className={styles.mobileNavbarRight}>
            {inventoryId && (
              <ShortlistButton
                inventoryId={inventoryId}
                inventory={inventory}
                noAbosolute
                screenName={APP_PAGE.PROPERTY}
                className={styles.mobileShortlistButton}
                iconColor="white"
              />
            )}
          </div>
        </div>

        {/* Media Area */}
        <div
          className={styles.mobileMediaArea}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Media item={currentItem} isDesktop={false} eager />
          {total > 1 && safeIndex > 0 && <NavArrow direction="prev" onClick={goPrev} />}
          {total > 1 && safeIndex < total - 1 && <NavArrow direction="next" onClick={goNext} />}
        </div>

        {/* Review card: fixed space at the bottom, image adjusts above */}
        {hasReviews && (
          <div
            className={formatClassNames(
              styles.mobileBottomSheet,
              reviewExpanded && styles.mobileBottomSheetExpanded,
            )}
          >
            <div className={styles.mobileReviewsContent}>
              {reviews
                .filter((review) => review.id === activeReviewId)
                .map((review) => (
                  <MobileGalleryReviewCard
                    key={review.id}
                    review={review}
                    expanded={reviewExpanded}
                    onToggleExpand={() => setReviewExpanded(!reviewExpanded)}
                  />
                ))}
            </div>
            {/* Room type bar */}
            {(() => {
              const activeReview = reviews.find((r) => r.id === activeReviewId);
              const roomTypeName = activeReview?.roomType || currentItem?.roomTypeName;
              if (!roomTypeName) return null;
              return (
                <div className={styles.mobileRoomTypeBar}>
                  <span className={styles.mobileRoomTypeName}>{roomTypeName}</span>
                  <button
                    type="button"
                    className={styles.mobileViewRoomsButton}
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent("close-reviews-panel"));
                      setTimeout(() => {
                        viewRoomsClickHandler("review_gallery");
                      }, 300);
                    }}
                    aria-label={`View rooms - ${roomTypeName}`}
                  >
                    {t("propertypage.components.gallerycarousel.viewrooms")}
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ReviewMediaGallery, {
  componentName: "ReviewMediaGallery",
  showForChina: true,
  showForWhiteLable: true,
});
